import { compareAsc, parse } from 'date-fns/esm';
import { filter, isEmpty, noop, get, orderBy } from 'lodash-es';
import {
  BOTTOMSHEET_MAPPER,
  FUNDS_STATUS,
  FUNDS_STATUS_VALUES,
  ORDER_STATUS,
  PURCHASE_ORDER_TYPE,
  STEP_STATUS,
  TRANSACTION_TYPE,
  TRANSACTION_TYPE_EVENTS_MAPPER,
  TRANSACTION_TYPE_MAPPER,
  YOUR_ORDERS_FILTER_TAB_OPTIONS,
  ORDER_SUCCESS_STATUS,
  SIP_DATA,
  PURCHASE_ORDER_DETAIL_TYPE,
} from '../../constants/portfolio';
import { ORDER_DETAILS } from '../../strings/portfolio';
import { formatAmountInr, numDifferentiationWithNoSpace, dateOrdinal } from '../common/functions';

export const isStatusPlaced = (status) => {
  return status === STEP_STATUS.ORDER_PLACED;
};

export const isStatusDeducted = (status) => {
  return status === STEP_STATUS.UNIT_DEDUCTED;
};

export const isStatusSwitched = (status) => {
  return status === STEP_STATUS.UNIT_SWITCHED;
};

export const isStatusAlloted = (status) => {
  return status === STEP_STATUS.UNIT_ALLOTED;
};

export const isSwitchPlaced = (status) => {
  return status === STEP_STATUS.SWITCH_PLACED;
};

export const isSwitchSuccess = (status) => {
  return status === STEP_STATUS.SWITCH_SUCCESS;
};

export const isProvUnitsAlloted = (status) => {
  return status === STEP_STATUS.PROV_UNITS_ALLOTED;
};

export const isRequestRaised = (status) => {
  return status === STEP_STATUS.REQUEST_RAISED;
};

export const isWithdrawSuccess = (status) => {
  return status === STEP_STATUS.WITHDRAW_SUCCESS;
};

export const isPurchaseSuccess = (status) => {
  return status === STEP_STATUS.PURCHASE_SUCCESS;
};

export const isDateAfterOrEqual = (date1, date2) => {
  return [0, 1].includes(compareAsc(date1, date2));
}

export const isDateBeforeOrEqual = (date1, date2) => {
  return [-1, 0].includes(compareAsc(date1, date2));
}

export const getFilteredOrders = (ordersData = [], filterData = {}, dateRange = {}) => {
  let filteredData = filter(ordersData, filterData);
  
  const { from_tdate = '', to_tdate = '' } = dateRange;

  if (from_tdate && to_tdate) {
    /*
      Using parse instead of directly using "new Date()"
      here to avoid device/broswer-specific formatting issues for
      new Date

      For reference: https://stackoverflow.com/a/3257513/8428072
    */
    const startDate = parse(from_tdate, 'yyyy/MM/dd', new Date());
    const endDate = parse(to_tdate, 'yyyy/MM/dd', new Date());

    filteredData = filter(filteredData, (order) => {
      const transDate = get(order, 'tran_date');

      if (transDate) {
        const orderDate = parse(transDate, 'dd MMM yyyy', new Date());
        return (
          isDateAfterOrEqual(orderDate, startDate) &&
          isDateBeforeOrEqual(orderDate, endDate)
        );
      } else {
        return false;
      }
    });
  }
  return filteredData;
};

export const getFilteredOrdersData = (ordersData = [], filterData = {}) => {
  const data = ordersData.map(({ options, isSuccessfull }) => {
    const filteredOrders = getFilteredOrders(options, filterData);
    return { options: filteredOrders, isSuccessfull };
  });
  return data;
};

export const isSwitchFlow = (type) => {
  const isSwitch = [TRANSACTION_TYPE.SWITCH, TRANSACTION_TYPE.SWITCHIN].includes(type);
  return isSwitch;
};

export const getStatusForFundData = (fundData = {}) => {
	const status = (!fundData.plutus_state && fundData.isSuccessfull) ? ORDER_SUCCESS_STATUS[fundData.ttype] : fundData.plutus_state;
	return status;
};

export const getInProgressOrderViewData = (data = {}, index = 0) => {
  const isSwitch = isSwitchFlow(data.ttype);
  let order = {
    type: getTransactionLabelName(data),
    status: 'IN PROGRESS',
    isSuccessfull: false,
    amount: numDifferentiationWithNoSpace(data.amount),
    dataAid: `inProgress${index + 1}`,
    icon: 'inprogress_icon.svg',
    date: data.tran_date,
  };
  const toMfData = data.to_mf || {};
  const fromMfData = data.from_mf || {};
  const mfData = data.mf || {};
  if (isSwitch) {
    order = {
      ...order,
      fromMfIsin: fromMfData.isin,
      toMfIsin: toMfData.isin,
      fromMfLogo: fromMfData.amc_logo_small,
      fromMfTitle: fromMfData.name,
      labelOne: 'Switch from',
      toMfLogo: toMfData.amc_logo_small,
      toMfTitle: toMfData.name,
      labelTwo: 'Switch to',
      isSwitch: true,
      statusDataAid: "inProgress",
    };
  } else {
    order = {
      ...order,
      title: mfData.name,
      logo: mfData.amc_logo_small,
      isSwitch: false,
      isin: mfData.isin,
    };
  }
  return order;
};

export const getOrderDetailsTransactionLabel = (fundData = {}) => {
  if (isEmpty(fundData)) {
    return "";
  }
  let label = "";
  if (fundData.ttype === TRANSACTION_TYPE.PURCHASE) {
    const isEmptyOrderType = isEmpty(fundData.order_type);
    let transactionType = FUNDS_STATUS_VALUES[fundData.ttype] || "";
    let orderType = "";
    if (!isEmptyOrderType) {
      transactionType = transactionType.toLowerCase();
      orderType = `${PURCHASE_ORDER_DETAIL_TYPE[fundData.order_type]} `;
    }
    if (fundData.payment_type === "auto_debit") {
      transactionType = "auto-debit"
    }
    label = `${orderType}${transactionType}`;
  } else {
    label = FUNDS_STATUS_VALUES[fundData.ttype];
  }
  return label;
};

export const getFundOrderViewData = (data = {}, index = 0) => {
  const isSwitch = isSwitchFlow(data.ttype);
  let order = {
    type: getTransactionLabelName(data),
    status: 'IN PROGRESS',
    isSuccessfull: false,
    amount: numDifferentiationWithNoSpace(data.amount),
    dataAid: `inProgress${index + 1}`,
    icon: 'inprogress_icon.svg',
    date: data.tran_date,
    transactionType: getOrderDetailsTransactionLabel(data),
  };
  if (data.isSuccessfull) {
    order.dataAid =  `sucessfull${index + 1}`;
    order.icon = 'successfull.svg';
    order.isSuccessfull = true;
    order.status = 'SUCCESSFUL';
    order.date = data.tdate;
  }
  const toMfData = data.to_mf || {};
  const fromMfData = data.from_mf || {};
  const mfData = data.mf || {};
  if (isSwitch) {
    order = {
      ...order,
      fromMfIsin: fromMfData.isin,
      toMfIsin: toMfData.isin,
      fromMfLogo: fromMfData.amc_logo_small,
      fromMfTitle: fromMfData.name,
      labelOne: 'Switch from',
      toMfLogo: toMfData.amc_logo_small,
      toMfTitle: toMfData.name,
      labelTwo: 'Switch to',
      isSwitch: true,
      statusDataAid: "inProgress",
    };
  } else {
    order = {
      ...order,
      title: mfData.name,
      logo: mfData.amc_logo_small,
      isSwitch: false,
      isin: mfData.isin,
    };
  }
  return order;
};

export const getSuccessfullOrderViewData = (data = {}, index = 0) => {
  const isSwitch = isSwitchFlow(data.ttype);
  let order = {
    isin: data.isin,
    date: data.tdate || data.tran_date,
    amount: numDifferentiationWithNoSpace(data.amount),
    type: TRANSACTION_TYPE_MAPPER[data.ttype],
    dataAid: `sucessfull${index + 1}`,
    icon: 'successfull.svg',
    isSuccessfull: true,
    status: 'SUCCESSFUL',
  };
  if (isSwitch) {
    order = {
      ...order,
      fromMfLogo: data.source_mf_logo,
      fromMfTitle: data.source_mf_name,
      fromMfIsin: data.source_mf_isin,
      labelOne: 'Switch from',
      toMfLogo: data.logo,
      toMfTitle: data.mf_name,
      toMfIsin: data.isin,
      labelTwo: 'Switch to',
      isSwitch: true,
      statusDataAid: "successful",
    };
  } else {
    order = {
      ...order,
      title: data.mf_name || data.mfname,
      logo: data.logo,
      isSwitch: false,
    };
  }
  return order;
};

export const getOrderViewData = (orderData, index, isSuccessfull) => {
  const data = {
    ...orderData,
    isSuccessfull,
  }
  const orderViewData = getFundOrderViewData(data, index);
  return orderViewData;
};

export const getFilterCount = (filterData = {}) => {
  let filterCount = 0;
  const filterOptions = ['order_type', 'ttype', 'fisdom_category_v2'];
  filterOptions.forEach((el) => {
    if (!isEmpty(filterData[el])) {
      filterCount++;
    }
  });
  return filterCount;
};

export const getFundHoldingsSipViewData = (data = {}) => {
  let viewData = SIP_DATA[data.friendly_status] || {};
  if (data.friendly_status === "active") {
    viewData.leftLabel = `${dateOrdinal(data.sip_day)} of every month`;
  }
  viewData = {
    ...viewData,
    amount: numDifferentiationWithNoSpace(data.amount),
    title: data.mfname,
    logo: data.logo,
    isin: data.isin,
  };
  return viewData;
};

export const getTransactionType = (transactionData = {}) => {
  const transType = FUNDS_STATUS_VALUES[transactionData.ttype];
  return transType;
};

export const getTransactionLabelName = (fundData = {}) => {
  let label = '';
  if (fundData.ttype === FUNDS_STATUS[0]) {
    label = `${
      !isEmpty(fundData.order_type) ? `${PURCHASE_ORDER_TYPE[fundData.order_type]} ` : ''
    }${FUNDS_STATUS_VALUES[fundData.ttype]}`;
  } else {
    label = FUNDS_STATUS_VALUES[fundData.ttype];
  }
  return label;
};

export const getTransactionTypeEventStatus = (orderData = {}) => {
  const ttype = orderData.ttype;
  let transactionType = TRANSACTION_TYPE_EVENTS_MAPPER[ttype] || ttype;
  if (ttype === FUNDS_STATUS[0]) {
    transactionType = `${
      !isEmpty(orderData.order_type) ? `${orderData.order_type} ` : ""
    }${transactionType}`;
  }
  return transactionType;
};

export const getWithdrawCardProgressData = (fundData = {}, ctaPress = noop) => {
  const status = getStatusForFundData(fundData);
  const isOrderPlaced = isStatusPlaced(status) || isStatusDeducted(status);
  const isUnitsDeducted = isStatusDeducted(status);
  const isSuccessfull = get(fundData,'isSuccessfull',false)
  const progressData = [
    {
      status: ORDER_STATUS.SUCCESS,
      title: ORDER_DETAILS.steps.withdrawReq,
      rightLabel: fundData.dt_created || ORDER_DETAILS.steps.immediate,
      subtitle: ORDER_DETAILS.steps.withdrawReqInfo,
      isNextStepCompleted: isOrderPlaced,
    },
    {
      status: isOrderPlaced ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.orderPlaced,
      rightLabel:
        fundData.dt_order_placed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.orderPlaceWithdrawInfo,
      isNextStepCompleted: isUnitsDeducted,
    },
    {
      status: isUnitsDeducted ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.unitsDeduct,
      rightLabel:
        fundData.dt_units_processed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.unitsDeductInfo,
      isNextStepCompleted: isUnitsDeducted,
    },
    {
      status: isSuccessfull ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.withdrawAmountCredit,
      rightLabel: fundData.dt_investment_confirmed || fundData.tdate,
      subtitle: ORDER_DETAILS.steps.withdrawAmountCreditInfo,
      onPressButton: ctaPress,
      buttonTitle: ORDER_DETAILS.steps.cta,
    },
  ];
  return progressData;
};

export const getPurchaseCardProgressData = (fundData = {}, ctaPress = noop) => {
  const status = getStatusForFundData(fundData);
  const isOrderPlaced = isStatusPlaced(status) || isStatusAlloted(status);
  const isUnitsAlloted = isStatusAlloted(status);
  const isSuccessfull = get(fundData,'isSuccessfull',false)
  const progressData = [
    {
      status: ORDER_STATUS.SUCCESS,
      title: ORDER_DETAILS.steps.paymentSuccess,
      rightLabel: fundData.dt_created || ORDER_DETAILS.steps.immediate,
      subtitle: ORDER_DETAILS.steps.paymentSuccessInfo,
      isNextStepCompleted: isOrderPlaced,
    },
    {
      status: isOrderPlaced ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.orderPlaced,
      rightLabel:
        fundData.dt_order_placed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.orderPlacedInfo,
      isNextStepCompleted: isUnitsAlloted,
    },
    {
      status: isUnitsAlloted ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.unitsAlloted,
      rightLabel:
        fundData.dt_units_processed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.unitsAllotedInfo,
      isNextStepCompleted: isUnitsAlloted,
    },
    {
      status: isSuccessfull ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.investConfirm,
      rightLabel: fundData.dt_investment_confirmed || fundData.tdate,
      subtitle: ORDER_DETAILS.steps.investConfirmInfo,
      onPressButton: ctaPress,
      buttonTitle: ORDER_DETAILS.steps.cta,
    },
  ];
  return progressData;
};

export const getSwitchCardProgressData = (fundData = {}, ctaPress = noop) => {
  const status = getStatusForFundData(fundData);
  const isOrderPlaced = isSwitchPlaced(status) || isStatusAlloted(status);
  const isUnitsAlloted = isStatusAlloted(status);
  const isSuccessfull = get(fundData, 'isSuccessfull', false)
  const progressData = [
    {
      status: ORDER_STATUS.SUCCESS,
      title: ORDER_DETAILS.steps.switchReq,
      rightLabel: fundData.dt_created || ORDER_DETAILS.steps.immediate,
      subtitle: ORDER_DETAILS.steps.switchReqInfo,
      isNextStepCompleted: isOrderPlaced,
    },
    {
      status: isOrderPlaced ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.orderPlaced,
      rightLabel:
        fundData.dt_order_placed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.orderPlaceWithdrawInfo,
      onPressButton: ctaPress,
      buttonTitle: ORDER_DETAILS.steps.cta,
      isNextStepCompleted: isUnitsAlloted,
    },
    {
      status: isUnitsAlloted ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.unitSwitch,
      rightLabel:
        fundData.dt_units_processed || ORDER_DETAILS.steps.oneWorkingData,
      subtitle: ORDER_DETAILS.steps.unitSwitchInfo,
      isNextStepCompleted: isUnitsAlloted,
    },
    {
      status: isSuccessfull ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      title: ORDER_DETAILS.steps.investConfirm,
      rightLabel: fundData.dt_investment_confirmed || fundData.tdate,
      subtitle: ORDER_DETAILS.steps.investConfirmSwitchInfo,
    },
  ];
  return progressData;
};

export const getOrderDetailsBottomSheetData = (fundData = {}) => {
  const { detail_info = {} } = fundData.stepper_view_data || {};
  const { view_data = {} } = detail_info || {};
  const { info_lines = [], title = "" } = view_data || {};
  const points = info_lines.map((data) => {
    let leftText = data.text,
      ctaText = "",
      rightText = "";
    const inlineCta = data.inline_cta || {};
    if (leftText.includes("$inline_cta")) {
      const text = leftText.split("$inline_cta")
      leftText = text[0];
      rightText = text[1] || "";
      ctaText = inlineCta.label;
    }
    return {
      leftText,
      rightText,
      ctaText,
      inlineCta,
    };
  });
  const bottomsheetData = {
    title: title,
    points,
  };
  return bottomsheetData;
};

export const getOrderDetailsFundViewData = (fundData = {}) => {
  const showStepsOrderStatus = [
    TRANSACTION_TYPE.PURCHASE,
    TRANSACTION_TYPE.REDEMPTION,
    TRANSACTION_TYPE.SWITCH,
    TRANSACTION_TYPE.SWITCHIN,
  ];
  const isSwitch = isSwitchFlow(fundData.ttype);
  const isWithdraw = TRANSACTION_TYPE.REDEMPTION === fundData.ttype;
  const isDivPayout = TRANSACTION_TYPE.DIVPAYOUT === fundData.ttype;
  const isPurchase = TRANSACTION_TYPE.PURCHASE === fundData.ttype;
  const isSIP = fundData.order_type === PURCHASE_ORDER_TYPE.sip.toLowerCase();
  const showUnits = fundData.isSuccessfull;
  const showNav = fundData.isSuccessfull && (isSwitch || isPurchase);
  const showStepsOrder = showStepsOrderStatus.includes(fundData.ttype);
  const showBankDetails = isDivPayout || isWithdraw;
  const endDate = fundData.dt_investment_confirmed || fundData.tdate;
  const isEasySip = isSIP && isPurchase; 
  const defaultData = getFundOrderViewData(fundData);
  
  let data = {
    ...defaultData,
    endDate,
    amount: formatAmountInr(fundData.amount),
    showStepsOrder,
    showBottomContent: false,
    dateDataAid: "date",
    showEasySip: isEasySip,
  };
  let mfData = {};
  if (isSwitch) {
    mfData = fundData.to_mf || {};
  } else {
    mfData = fundData.mf || {};
  }
  if (showUnits && fundData.units) {
    const units = fundData.units;
    data.units = `${units} units`;
  }
  const nav = fundData.nav || mfData.curr_nav;
  if (showNav && nav) {
    data.nav = `₹${nav}`;
  }
  if (isWithdraw) {
    let exitLoadAmount = "";
    let withdrawAmount = fundData.amount;
    const exitLoadValue = fundData.exit_load;
    if (exitLoadValue || exitLoadValue === 0) {
      if (typeof exitLoadValue === "number") {
        if (fundData.isSuccessfull) {
          withdrawAmount = Number(fundData.amount) + Number(exitLoadValue);
        } else {
          withdrawAmount = Number(fundData.amount) - Number(exitLoadValue);
        }
        exitLoadAmount =`- ${formatAmountInr(exitLoadValue)}`;
      } else {
        exitLoadAmount = exitLoadValue
      }
    } else {
      exitLoadAmount = "N.A"
    }
    if (fundData.isSuccessfull) {
      data.amount = formatAmountInr(withdrawAmount);
      data.withdrawAmount = formatAmountInr(fundData.amount);
    } else {
      data.withdrawAmount = formatAmountInr(withdrawAmount);
    }
    data.exitLoadAmount = exitLoadAmount;
    data.exitLoadPercentage = fundData.exit_load_percent;
  }
  if (showBankDetails) {
    let bankAcc = get(fundData,'bank_account','')
    if(!bankAcc){
      bankAcc = get(fundData,'bank_account_no','')
    }
    bankAcc = bankAcc.split('*').join("•") 
    data.bankAccount = bankAcc;
  }
  return data;
};

export const getFundOrderStepsData = (fundData = {}) => {
  const { steps = [], detail_info = {} } = fundData.stepper_view_data || {};
  const { cta_label = "" } = detail_info || {};
  const orderSteps = steps.map((data, index) => {
    const isLastStep = index === steps.length - 1;
    let isNextStepCompleted = false;
    if (!isLastStep) {
      const nextStepContent = steps[index + 1] || {};
      isNextStepCompleted = nextStepContent.completed;
    }

    let viewData = {
      ...data,
      status: data.completed ? ORDER_STATUS.SUCCESS : ORDER_STATUS.DEFAULT,
      isNextStepCompleted,
      rightLabel: data.right_label,
    };
    if (isLastStep) {
      viewData.buttonTitle = cta_label;
    }
    return viewData;
  });
  return orderSteps;
};

export const getYourOrdersFilterTabOptions = (isSuccessfull) => {
  if (isSuccessfull) {
    const data = YOUR_ORDERS_FILTER_TAB_OPTIONS.filter((el) => el.id !== 1);
    return data;
  }
  return YOUR_ORDERS_FILTER_TAB_OPTIONS;
};

export const getYourFundViewData = (
  yourFundsSummary = [],
  yourFundData = {}
) => {
  const funds = `${yourFundsSummary.length} funds`;
  const totalAmount = numDifferentiationWithNoSpace(
    yourFundData.total_current_amount
  );
  let xirrData = {};
  xirrData.xirr = `${yourFundData.xirr.toFixed(2) || 0}%`;
  if (yourFundData.xirr > 0) {
    xirrData.color = "foundationColors.secondary.profitGreen.400";
    xirrData.xirr = `+${xirrData.xirr}`;
  } else if (yourFundData.xirr < 0) {
    xirrData.color = "foundationColors.secondary.lossRed.400";
  }
  return {
    funds,
    totalAmount,
    xirrData,
  };
};

export const getFundHoldingsViewData = (fundHoldingData) => {
  let xirrData = {
    iconDataAid: "growth",
  };
  if (fundHoldingData.xirr > 0) {
    xirrData.color = "foundationColors.secondary.profitGreen.400";
    xirrData.icon = "positive_return.svg";
    xirrData.title = `+${fundHoldingData.xirr.toFixed(2)}%`;
  } else if (fundHoldingData.xirr < 0) {
    xirrData.color = "foundationColors.secondary.lossRed.400";
    xirrData.icon = "negative_return.svg";
    xirrData.title = `${fundHoldingData.xirr.toFixed(2)}%`;
  } else {
    xirrData.title = `${fundHoldingData.xirr.toFixed(2)}%`;
  }
  const riskType = fundHoldingData.risk_type || "";
  const fisdomCategoryV2 = fundHoldingData.fisdom_category_v2 || "";
  const fisdomSubcategoryV2 = fundHoldingData.fisdom_subcategory_v2 || "";
  const folioNumbers = fundHoldingData.folio_numbers || [];
  const data = {
    title: fundHoldingData.fund_name,
    logo: fundHoldingData.logo,
    tagsData: [
      { name: riskType.split("_").join(" ") },
      { name: fisdomCategoryV2.split("_").join(" ") },
      { name: fisdomSubcategoryV2.split("_").join(" ") },
    ],
    currentAmount: numDifferentiationWithNoSpace(
      fundHoldingData.current_amount
    ),
    currentInrAmount: formatAmountInr(fundHoldingData.current_amount),
    investedAmount: numDifferentiationWithNoSpace(
      fundHoldingData.invested_amount
    ),
    investedInrAmount: formatAmountInr(fundHoldingData.invested_amount),
    unrealisedGain: numDifferentiationWithNoSpace(fundHoldingData.unrealised_gain),
    xirrData,
    averageNav: fundHoldingData.average_nav,
    totalUnits: fundHoldingData.total_units,
    realisedGain: numDifferentiationWithNoSpace(fundHoldingData.realised_gain),
    lockInPeriod: fundHoldingData.lockin_period,
    folioNumbers: folioNumbers.join(", "),
  };
  return data;
};


// Your Funds Filter Functions

export const getFilteredFundsByFundHouses = (fundHouses = [], funds = []) => {
  const updatedFilteredFunds = funds.filter((fund) => {
    return fundHouses.includes(fund.amc_name);
  });
  return updatedFilteredFunds;
};

export const getFilteredFundsByCategories = (formatedCategoryValues = [], funds = []) => {
  const filteredFunds = funds.filter((fund) => {
    let isSelected = false;
    formatedCategoryValues.forEach((formatedCategoryValue) => {
      if (fund.fisdom_category_v2 === formatedCategoryValue.fisdom_category_v2) {
        const fisdomSubcategoryV2 = get(formatedCategoryValue,'fisdom_subcategory_v2', []);
        fisdomSubcategoryV2.forEach((el) => {
          if (fund.fisdom_subcategory_v2 === el) {
            isSelected = true;
          }
        });
      }
    });
    return isSelected;
  });
  return filteredFunds;
};

export const formatSelectedCategories = (categoryValues = []) => {
  const formatedCategories = [];
  let formatedCategoriesLength = 0;
  categoryValues.forEach((category) => {
    const newCategory = {};
    const categoryValue = get(category,'value');
    newCategory.fisdom_category_v2 = categoryValue;
    newCategory.fisdom_subcategory_v2 = [];
    const subcategories = get(category, 'subcategories', []);
    subcategories.map((subCategory) => {
      const subCategoryValue = get(subCategory, 'value');
      const isSelected = get(subCategory, 'isSelected');
      if (isSelected) {
        formatedCategoriesLength++;
        newCategory.fisdom_subcategory_v2.push(subCategoryValue);
      }
    });
    if (!isEmpty(newCategory.fisdom_subcategory_v2)) {
      formatedCategories.push(newCategory);
    }
  });

  return { formatedCategories, formatedCategoriesLength };
};

export const getFilterCountFilteredSummary = (
  funds = [],
  selectedSortItem,
  selectedFundHouseValues=[],
  selectedCategoryValues
) => {
  let tempFundList = [...funds];

  // filter funds by sorted item
  if (!isEmpty(selectedSortItem)) {
    const { type = '', order = '' } = selectedSortItem;
    tempFundList = orderBy(tempFundList, [type], [order]);
  }

  // filter funds by fund houses
  if (!isEmpty(selectedFundHouseValues)) {
    tempFundList = getFilteredFundsByFundHouses(selectedFundHouseValues, tempFundList);
  }

  // filter funds by categories
  const { formatedCategories, formatedCategoriesLength } =
    formatSelectedCategories(selectedCategoryValues);
  if (!isEmpty(formatedCategories)) {
    tempFundList = getFilteredFundsByCategories(formatedCategories, tempFundList);
  }

  const filterCountValue = selectedFundHouseValues.length + formatedCategoriesLength;
  return {
    filteredFundList: tempFundList,
    filterCountValue,
  };
};

export const verifyWrongExpiredOtp = (errorMessage = "") => {
  return errorMessage.match(/wrong OTP|expire/i);
};